import React, { useEffect, useState } from "react";
import { X, ChevronDown, ChevronUp } from "lucide-react";
import "../../../../styles/useCustomScrollbar";
import useCustomScrollbar from "../../../../styles/useCustomScrollbar";
import { ReactComponent as ExcellentPoopIcon } from "../../../../../assects/svg/client_details/GUT_HEALTH/Excellent_poop.svg";
import { ReactComponent as GoodPoopIcon } from "../../../../../assects/svg/client_details/GUT_HEALTH/Good_poop.svg";
import { ReactComponent as SatisfactoryPoopIcon } from "../../../../../assects/svg/client_details/GUT_HEALTH/Satisfactory_poop.svg";
import { ReactComponent as HardPoopIcon } from "../../../../../assects/svg/client_details/GUT_HEALTH/Hard_poop.svg";
import { ReactComponent as PoorPoopIcon } from "../../../../../assects/svg/client_details/GUT_HEALTH/Poor_poop.svg";
import { ReactComponent as NoPoopIcon } from "../../../../../assects/svg/client_details/GUT_HEALTH/No_poop.svg";

type PoopStatus = "Excellent" | "Good" | "Satisfactory" | "Hard" | "Poor" | "No poop";
type DayStatus = "logged" | "not-logged" | "no-cycle";

export interface PoopEntry {
  status: PoopStatus;
  time: string;
  message?: string;
}

export interface GutHealthDayLog {
  dayLabel: string; // e.g. "MO, 20 MAY"
  status: DayStatus;
  entries: PoopEntry[];
}

interface GutHealthDetailPopupProps {
  open: boolean;
  onClose: () => void;
  days?: GutHealthDayLog[];
}

// Reuse the same status → SVG icon mapping used by GutHealthCard
const STATUS_ICON: Record<
  PoopStatus,
  React.FunctionComponent<React.SVGProps<SVGSVGElement>>
> = {
  Excellent: ExcellentPoopIcon,
  Good: GoodPoopIcon,
  Satisfactory: SatisfactoryPoopIcon,
  Hard: HardPoopIcon,
  Poor: PoorPoopIcon,
  "No poop": NoPoopIcon,
};

// ── Day row ───────────────────────────────────────────────────────────────
const DayRow: React.FC<{ day: GutHealthDayLog; expanded: boolean; onToggle: () => void }> = ({
  day,
  expanded,
  onToggle,
}) => {
  const isLogged = day.status === "logged";
  const rightLabel =
    day.status === "logged"
      ? `${day.entries.length} ${day.entries.length === 1 ? "cycle" : "cycles"}`
      : day.status === "not-logged"
      ? "Not logged"
      : "No cycle";

  return (
    <div className="border-b border-[#F3F4F6] last:border-b-0">
      <button
        type="button"
        onClick={isLogged ? onToggle : undefined}
        disabled={!isLogged}
        className={`w-full flex items-center justify-between px-5 py-4 text-left ${
          isLogged ? "hover:bg-[#FAFAFA] cursor-pointer" : "cursor-default"
        } transition`}
      >
        <span className="text-[13px] font-semibold uppercase tracking-wide text-[#1F1F1F] font-inter">
          {day.dayLabel}
        </span>

        <div className="flex items-center gap-3">
          <span
            className={`text-[13px] font-inter ${
              isLogged ? "text-[#1F1F1F] font-semibold" : "text-[#8D8D8D]"
            }`}
          >
            {rightLabel}
          </span>
          {isLogged && (
            expanded ? (
              <ChevronUp className="w-4 h-4 text-[#8D8D8D]" />
            ) : (
              <ChevronDown className="w-4 h-4 text-[#8D8D8D]" />
            )
          )}
        </div>
      </button>

      {expanded && isLogged && (
        <div className="px-5 pb-4 flex flex-col gap-3">
          {day.entries.map((entry, i) => {
            const Icon = STATUS_ICON[entry.status] ?? NoPoopIcon;
            return (
              <div key={i} className="flex items-start gap-3">
                <Icon className="w-8 h-8 flex-shrink-0" />
                <div className="flex flex-col">
                  <div className="flex items-center gap-2">
                    <span className="text-[13px] font-semibold text-[#1F1F1F] font-inter">
                      {entry.status}
                    </span>
                    <span className="w-1 h-1 rounded-full bg-[#C8C8C8]" />
                    <span className="text-[12px] text-[#8D8D8D] font-inter">{entry.time}</span>
                  </div>
                  <span className="text-[12px] text-[#8D8D8D] font-inter mt-0.5">
                    {entry.message ?? "No message"}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ── Popup ─────────────────────────────────────────────────────────────────
const GutHealthDetailPopup: React.FC<GutHealthDetailPopupProps> = ({
  open,
  onClose,
  days = [],
}) => {
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  useCustomScrollbar();

  // Auto-expand the first day (Monday) whenever the popup opens
  useEffect(() => {
    if (open) setExpanded(new Set([0]));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const toggle = (i: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40 p-6"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-[560px] max-h-[85vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-3 border-b border-[#F3F4F6]">
          <h2 className="text-[16px] 3xl:text-[18px] font-semibold uppercase tracking-widest text-[#1F1F1F] font-inter">
            Gut Health
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-full hover:bg-[#F3F4F6] transition text-[#1F1F1F]"
            aria-label="close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto custom-scrollbar-overlay">
          {days.map((day, i) => (
            <DayRow
              key={day.dayLabel}
              day={day}
              expanded={expanded.has(i)}
              onToggle={() => toggle(i)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default GutHealthDetailPopup;
